import { Alert, Platform } from 'react-native';

/**
 * Cross-platform destructive confirm. On native it uses `Alert.alert` with
 * two buttons; on web it falls back to `window.confirm`, because RN-Web
 * silently collapses multi-button `Alert.alert` into a single OK button —
 * which means the destructive branch would never fire.
 *
 * Always call this instead of `Alert.alert(...)` whenever the user is
 * confirming a destructive action that needs to work on the web shell too.
 *
 * @param title       short header ("Delete this voucher?")
 * @param message     longer body explaining consequences
 * @param onConfirm   fired only when the user explicitly confirms
 * @param confirmLabel button label on native (web shows "OK")
 */
export function confirmDestructive(
  title: string,
  message: string,
  onConfirm: () => void,
  confirmLabel = 'Delete',
): void {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined' && window.confirm(`${title}\n\n${message}`)) {
      onConfirm();
    }
    return;
  }
  Alert.alert(title, message, [
    { text: 'Cancel', style: 'cancel' },
    { text: confirmLabel, style: 'destructive', onPress: onConfirm },
  ]);
}

/**
 * Cross-platform NON-destructive confirm for a positive choice (purchases,
 * subscribing, etc.). Same web-safety as {@link confirmDestructive} — RN-Web
 * collapses multi-button Alerts — but the native button uses a default (not red)
 * style and the wording frames it as confirming a choice, not warning about one.
 *
 * @param title       upbeat header ("Start your membership?")
 * @param message     what they're confirming ("3 Ultimate washes for $24 …")
 * @param onConfirm   fired only when the user explicitly confirms
 * @param confirmLabel native confirm button label (web shows "OK")
 * @param cancelLabel  native cancel button label
 */
export function confirmChoice(
  title: string,
  message: string,
  onConfirm: () => void,
  confirmLabel = 'Confirm',
  cancelLabel = 'Not now',
): void {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined' && window.confirm(`${title}\n\n${message}`)) {
      onConfirm();
    }
    return;
  }
  Alert.alert(title, message, [
    { text: cancelLabel, style: 'cancel' },
    { text: confirmLabel, style: 'default', onPress: onConfirm },
  ]);
}
